import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCDropDownItem, 
	RCCIcon,
	RCCInput,
	RCCRefModalProps
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import LovIndicators from "src/components/Lov/Indicators";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import Modal from "src/components/Modal";
import { ModalCenter } from "src/components/Modal/styles";
import { css } from "styled-components";
import ModalFields from "./modals";
import { IRefIndicatorFieldModals } from "./interface";

const IndicatorFields: React.FC = () => { 
	const { translate } = useTranslation();
	const refModals = useRef<IRefIndicatorFieldModals>(null);

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string ,indicator?:number}>({ filter: "" });
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		control:controlFilters,
		reset: resetFilters, 
		watch: watchFilters 
	} = useForm();

	const submitFilters = (data: any) => {
		setFilters(() => {
			if(!data.indicator)
				return {filter: data.filter};
			return { 
				filter: data.filter, 
				indicator: typeof data.indicator === "object" ? data.indicator?.value : data.indicator 
			};
		});
		paginationRef.current?.reload();
	};

	// modal onde é usado
	const paginationModalWhereRef = useRef<IForwardPagination>(null);
	const refModalWhere = useRef<RCCRefModalProps>(null);
	const [fieldId, setFieldId] = useState<number>();

	// List & Pagination
	const paginationRef = useRef<IForwardPagination>(null);
	const colOptions = (row: any) => (
		<OptionsColumn>
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				<RCCDropDownItem
					selfEnclose
					onClick={() => { refModals.current?.actionButtonChange(row);  }}
				>
					<RCCIcon 
						name="pencil" 
					/>
					{translate("Alterar")}
				</RCCDropDownItem>
				<RCCDropDownItem
					selfEnclose
					onClick={() => { refModals.current?.actionButtonChange(row,undefined,undefined,true);  }}
				>
					<RCCIcon 
						name="copy" 
					/>
					{translate("Copiar")}
				</RCCDropDownItem>
				
			</CheckPermission>
			<RCCDropDownItem 
				selfEnclose
				onClick={() => { 
					setFieldId(row.iqb001Id);
					paginationModalWhereRef.current?.reload();
					refModalWhere.current?.openModal();
				}}
			>
				<RCCIcon 
					name="list" 
				/>
				{`${translate("Onde é usado")}?`}
			</RCCDropDownItem>
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => { refModals.current?.actionButtonDelete(row); }}
				>
					<RCCIcon 
						name="trash" 
					/>
					{translate("Excluir")}
				</RCCDropDownItem>
			</CheckPermission>
		</OptionsColumn>
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Campos dos Indicadores")}
					icon={() => <AppIcons name="indicators" />}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Novo campo")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => { refModals.current?.actionButtonAdd(); }}
								size="md"
							/>
						</RCCTooltip>
					</CheckPermission>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || filters.filter || filters.indicator ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || filters.filter || filters.indicator ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>

				{/* Filters */}
				
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ filter: "",indicator:{value:"",label:""}})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
							xs={10}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
								lg={3}
							>
								<LovIndicators
									control={controlFilters}
									name="indicator"
									label={translate("Indicador")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
								md={4}
								lg={4}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="IndicatorsFields/List"
					styleInline={{ulContainer:"padding-top:1.6rem;"}}
					filtersData={filters}    
					columns={[
						{
							idColumn: "iqb001Descricao",
							nameColumn: translate("Descrição"),
							xs: 12,
							sm: 4,
							md: 4,
							type: "string",
							truncate:true
						},
						{
							idColumn: "iqb001Codigo",
							nameColumn: translate("Código"),
							xs: 12,
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "iqb001Label",
							nameColumn: translate("Rótulo"),
							xs: 12,
							sm: 4,
							md: 4,
							type: "string"
						},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colOptions,
							disableSort: true
						}
					]}
				/>
			</RCCGrid>

			<ModalFields ref={refModals} paginationRef={paginationRef} />
			
			<Modal
				ref={refModalWhere}
				header={{
					title:`${translate("Onde é usado")}?`,
					icon:{name:"list"}
				}}
				footer={{
					showSaveButton: false,
					cancelButtonText: translate("Fechar")
				}}
				styleInline={`${ModalCenter} height: 660px;`}
			>
				<Pagination 
					ref={paginationModalWhereRef}
					dataOrRoute="IndicatorsFields/ListIndicators"
					filtersData={{fieldId}}    
					styleInline={{
						row: css`
							>div{
								>div:last-child{
									justify-content: unset;
								}
							}
						`
					}}
					columns={[
						{
							idColumn: "iqc001Codigo",
							nameColumn: translate("Código"),
							xs: 12,
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "iqc001Descricao",
							nameColumn: translate("Descrição"),
							xs: 12,
							sm: 6,
							md: 6,
							type: "string",
							justify:"left"
						}
					]}
				/>
			</Modal>
		</>
	);
};

export default IndicatorFields;
